<template>
  <div class="harbor-shell harbor-shell--chat">
    <section class="harbor-main">
      <!-- Header -->
      <header class="harbor-chat__header">
        <a class="harbor-chat__back" :href="indexHref" aria-label="Back to messages">
          <i class="fa-solid fa-chevron-left" aria-hidden="true"></i>
        </a>
        <div class="harbor-chat__header-avatar" :style="{ backgroundColor: avatarColor }">
          <img v-if="otherAvatarUrl"
               :src="otherAvatarUrl"
               :alt="group.name"
               class="harbor-chat__header-avatar-img"
               @error="avatarError = true" />
          <i v-else-if="!isDirectPair" class="fa-solid fa-users" aria-hidden="true"></i>
          <template v-else>{{ avatarInitials }}</template>
        </div>
        <div class="harbor-chat__title-block">
          <h2 class="harbor-chat__title">{{ group.name }}</h2>
          <p class="harbor-chat__subtitle">{{ subtitleText }}</p>
        </div>
        <div class="harbor-chat__header-actions">
          <button type="button"
                  class="harbor-chat__icon-btn harbor-chat__info-btn"
                  data-cy="chat-info-btn"
                  aria-label="Group details"
                  @click="showDetailsModal = true">
            <i class="fa-solid fa-circle-info" aria-hidden="true"></i>
          </button>
          <button type="button"
                  class="harbor-chat__icon-btn"
                  :class="{ 'harbor-chat__icon-btn--active': pinned }"
                  data-cy="chat-pin-btn"
                  :style="{ opacity: settingOpacity('pinned') }"
                  :aria-label="pinned ? 'Unpin group' : 'Pin group'"
                  :aria-pressed="pinned ? 'true' : 'false'"
                  @click.prevent="toggleSetting('pinned')">
            <i class="fa-solid fa-thumbtack" aria-hidden="true"></i>
          </button>
          <button type="button"
                  class="harbor-chat__icon-btn"
                  :class="{ 'harbor-chat__icon-btn--active': muted }"
                  data-cy="chat-mute-btn"
                  :style="{ opacity: settingOpacity('muted') }"
                  :aria-label="muted ? 'Unmute group' : 'Mute group'"
                  :aria-pressed="muted ? 'true' : 'false'"
                  @click.prevent="toggleSetting('muted')">
            <i :class="muted ? 'fa-solid fa-bell-slash' : 'fa-solid fa-bell'" aria-hidden="true"></i>
          </button>
          <div v-if="admin" class="btn-group">
            <button data-toggle="dropdown" class="harbor-chat__icon-btn" aria-label="More options" aria-expanded="false">
              <i aria-hidden="true" class="fa-solid fa-ellipsis-vertical"></i>
              <span class="sr-only">Toggle Dropdown</span>
            </button>
            <ul class="dropdown-menu right-aligned">
              <li v-if="canChangeMembers">
                <a :href="editLink">Edit...</a>
              </li>
            </ul>
          </div>
        </div>
      </header>

      <!-- Tabs (scheduled is an author-only view; the live draft lives inline in
           the composer, iMessage-style, so there is no drafts tab) -->
      <nav v-if="canPublish" class="harbor-chat__tabs" role="tablist">
        <button type="button"
                class="harbor-chat__tab"
                :class="{ 'harbor-chat__tab--active': activeTab === 'conversation' }"
                data-cy="tab-conversation"
                role="tab"
                :aria-selected="activeTab === 'conversation'"
                @click="setActiveTab('conversation')">
          Messages
        </button>
        <button type="button"
                class="harbor-chat__tab"
                :class="{ 'harbor-chat__tab--active': activeTab === 'scheduled' }"
                data-cy="tab-scheduled"
                role="tab"
                :aria-selected="activeTab === 'scheduled'"
                @click="setActiveTab('scheduled')">
          Scheduled<span v-if="scheduled.length > 0" class="harbor-chat__tab__count" data-cy="tab-scheduled-count">{{ scheduled.length }}</span>
        </button>
      </nav>

      <!-- Scrollable message list -->
      <div class="harbor-chat__scroller" ref="scroller" data-cy="chat-scroller" v-show="activeTab === 'conversation'">
        <div v-if="!noMoreData" class="harbor-chat__load-more">
          <a v-if="!loadingMore && !loading" @click.prevent="getMoreMessages()"><small>Load more...</small></a>
          <div class="loading-inline p-h-sm">
            <small v-if="loadingMore">Loading...</small>
            <spinner v-if="loadingMore" :inline="true"></spinner>
          </div>
        </div>
        <div v-if="loading" class="text-center p-m">
          <spinner></spinner>
        </div>
        <small v-if="error"><span class="text-danger">{{ errorMessage }}</span></small>

        <template v-for="entry in groupedMessages">
          <div v-if="entry.type === 'divider'" :key="`d-${entry.day}`" class="chat-day-divider">
            {{ entry.label }}
          </div>
          <message v-else
                   :key="`m-${entry.message.id}`"
                   :message="entry.message"
                   :active-profile-id="activeProfileId"
                   :active-profile-type="activeProfileType"
                   :reactions-enabled="reactionsEnabled"
                   :is-coach="admin"
                   :is-pair="isPair"
                   :first-of-run="entry.firstOfRun"
                   :highlighted="entry.message.id === highlightMessageId"
                   @edit-message="editSentMessage"></message>
        </template>

        <div v-if="isEmpty()" class="harbor-chat__empty">
          <p class="m-b-sm">🤼 No messages have been sent yet, but stay tuned.</p>
          <p v-if="wiqAppInstalled" class="m-b-none">
            You'll get push notifications in the WrestlingIQ app when new messages arrive.
          </p>
          <template v-else>
            <p class="m-b-sm">
              Install the WrestlingIQ app to get push notifications for new messages.
            </p>
            <div class="harbor-chat__empty-badges">
              <a href="https://apps.apple.com/us/app/wrestlingiq/id1493740351"
                 target="_blank"
                 rel="noopener">
                <img src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?releaseDate=1652918400&h=f1999a9a284ededf8cf930cb00e37e95"
                     alt="Download on the App Store"
                     class="harbor-chat__empty-badge" />
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.athleticsiq.android.wrestlingiq"
                 target="_blank"
                 rel="noopener">
                <img src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                     alt="Get it on Google Play"
                     class="harbor-chat__empty-badge harbor-chat__empty-badge--play" />
              </a>
            </div>
          </template>
        </div>
      </div>

      <!-- Scheduled list -->
      <div v-show="activeTab === 'scheduled'" class="harbor-chat__scroller">
        <div v-if="queueLoading" class="text-center p-m">
          <spinner></spinner>
        </div>
        <div v-else-if="scheduled.length === 0" class="msg-queue-empty">
          No scheduled messages. Use the clock icon in the composer to queue one up.
        </div>
        <div v-else class="msg-queue-list">
          <div v-for="msg in scheduled" :key="`sched-${msg.id}`" class="msg-queue-item" data-cy="scheduled-item">
            <div class="msg-queue-item__meta">
              <span class="msg-queue-item__badge msg-queue-item__badge--scheduled">
                <i class="fa-regular fa-clock" aria-hidden="true"></i>
                Sending {{ formatScheduledAt(msg.scheduled_at) }}
              </span>
              <span>Created {{ formatTimestamp(msg.created_at) }}</span>
            </div>
            <div v-if="previewForQueueItem(msg)"
                 class="msg-queue-item__preview"
                 v-html="previewForQueueItem(msg)"></div>
            <message-attachments :attachments="msg.attachments"></message-attachments>
            <div class="msg-queue-item__actions">
              <button type="button" class="btn btn-default btn-compact" data-cy="scheduled-edit" @click="openItemEditor(msg)">Edit</button>
              <button type="button" class="btn btn-default btn-compact" data-cy="scheduled-delete" @click="deleteQueueItem(msg)">Delete</button>
              <button type="button" class="btn btn-primary btn-compact" data-cy="scheduled-send-now" @click="sendScheduledNow(msg)">Send now</button>
            </div>
          </div>
        </div>
      </div>

      <!-- Composer (or announcement-only notice) -->
      <div v-if="canPublish" class="harbor-chat__composer" data-cy="chat-composer">
        <message-form
            :group="group"
            :initial-live-draft="liveDraft"
            :live-draft-provided="liveDraftProvided"
            :endpoint="endpoint"
            :access-key="accessKey"
            :auth-token="authToken"
            :polls-enabled="pollsEnabled"
            @scheduled-saved="onScheduledSaved"
        ></message-form>
      </div>
      <div v-else class="harbor-chat__locked" data-cy="chat-locked">
        This is an announcement only group. If you have a question, tap the sender's name to message them directly.
      </div>
    </section>

    <!-- Details panel (xl desktop only) -->
    <aside class="harbor-details">
      <chat-details
        :group="group"
        :admin="admin"
        :member-count="memberCount"
        :show-member-count="showMemberCount"
        :member-url="memberUrl"
        @sync-click="openRosterSync"
      />
    </aside>

    <!-- Details modal (shown below xl when user taps info button) -->
    <div v-if="showDetailsModal"
         class="roster-sync-modal"
         role="dialog"
         aria-modal="true"
         aria-labelledby="chat-details-title">
      <div class="roster-sync-modal__backdrop" @click="showDetailsModal = false"></div>
      <div class="roster-sync-modal__shell">
        <header class="roster-sync-modal__header">
          <div>
            <h2 id="chat-details-title" class="roster-sync-modal__title">Group details</h2>
          </div>
          <button type="button"
                  class="harbor-chat__icon-btn"
                  aria-label="Close"
                  @click="showDetailsModal = false">
            <i class="fa-solid fa-xmark" aria-hidden="true"></i>
          </button>
        </header>
        <div class="roster-sync-modal__body">
          <chat-details
            :group="group"
            :admin="admin"
            :member-count="memberCount"
            :show-member-count="showMemberCount"
            :member-url="memberUrl"
            @sync-click="onSyncClickFromModal"
          />
        </div>
      </div>
    </div>

    <roster-sync-modal
      v-if="showRosterSync"
      :group="group"
      @close="showRosterSync = false"
      @saved="onRosterSyncSaved"
    ></roster-sync-modal>

    <!-- Editor for a scheduled message or a sent-message edit. A separate composer
         instance so it never disturbs the inline live draft. -->
    <div v-if="showItemEditor"
         class="roster-sync-modal"
         data-cy="item-editor-modal"
         role="dialog"
         aria-modal="true"
         aria-labelledby="item-editor-title">
      <div class="roster-sync-modal__backdrop" @click="closeItemEditor"></div>
      <div class="roster-sync-modal__shell">
        <header class="roster-sync-modal__header">
          <h2 id="item-editor-title" class="roster-sync-modal__title">{{ itemEditorTitle }}</h2>
          <button type="button"
                  class="harbor-chat__icon-btn"
                  aria-label="Close"
                  @click="closeItemEditor">
            <i class="fa-solid fa-xmark" aria-hidden="true"></i>
          </button>
        </header>
        <div class="roster-sync-modal__body">
          <message-form
              instance-key="-editor"
              :auto-load-draft="false"
              :initial-item="editorItem"
              :group="group"
              :endpoint="endpoint"
              :access-key="accessKey"
              :auth-token="authToken"
              :polls-enabled="pollsEnabled"
              @scheduled-saved="onItemEditorSaved"
              @message-sent-from-queue="onItemEditorSaved"
              @message-edited="onItemEditorEdited"
              @cancel-edit="closeItemEditor"
          ></message-form>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import LocalTime from 'local-time';
import moment from 'moment';
import Spinner from './spinner.vue';
import MessageForm from './message_form.vue';
import Message from './message.vue';
import MessageAttachments from './message_attachments.vue';
import ChatDetails from './chat_details.vue';
import RosterSyncModal from './roster_sync_modal.vue';
import { avatarInitials, avatarColor } from '../utilities/avatars';

// The only thing that differs between the pin and mute toggles: their copy.
const MEMBERSHIP_SETTINGS = {
  pinned: { on: 'Pinned', off: 'Unpinned', noun: 'pin' },
  muted: { on: 'Muted', off: 'Unmuted', noun: 'mute' },
};

export default {
  components: {
    Message,
    MessageAttachments,
    Spinner,
    MessageForm,
    ChatDetails,
    RosterSyncModal,
  },
  name: 'chat',
  props: {
    group: {
      type: Object,
      required: true,
    },
    // The viewer's live draft for this group, server-rendered into the page.
    // null is meaningful ("this viewer has no draft"), so it is paired with
    // liveDraftProvided rather than inferred from falsiness.
    liveDraft: {
      type: Object,
      default: null,
    },
    liveDraftProvided: {
      type: Boolean,
      default: false,
    },
    admin: {
      type: Boolean,
      default: false,
    },
    membership: {
      type: Object,
      required: true,
    },
    activeProfileId: {
      type: Number,
      required: true,
    },
    activeProfileType: {
      type: String,
      required: true,
    },
    endpoint: {
      type: String,
      required: true,
    },
    accessKey: {
      type: String,
      required: true,
    },
    authToken: {
      type: String,
      required: true,
    },
    reactionsEnabled: {
      type: Boolean,
      default: false,
    },
    pollsEnabled: {
      type: Boolean,
      default: false,
    },
    wiqAppInstalled: {
      type: Boolean,
      default: false,
    },
    highlightMessageId: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      loading: false,
      loadingMore: false,
      noMoreData: false,

      nextPageUrl: null,
      page: 1,
      perPage: 30,

      messagesLoaded: false,
      count: 0,
      messages: [],

      error: false,
      errorMessage: '',

      memberCount: 0,
      showMemberCount: false,

      pollInterval: null,

      muted: false,
      pinned: false,
      // Keyed by membership setting so both header toggles share one in-flight
      // guard and one dimming rule. Keys are pre-declared for Vue 2 reactivity.
      updatingSetting: { pinned: false, muted: false },

      activeTab: 'conversation',
      scheduled: [],
      queueLoading: false,
      scheduledLoadedOnce: false,
      // Shared editor modal for scheduled messages and sent-message edits — a
      // separate composer instance so neither disturbs the inline live draft.
      showItemEditor: false,
      editorItem: null,

      showRosterSync: false,
      showDetailsModal: false,

      avatarError: false,
    };
  },
  created() {
    const vm = this;
    vm.getMessages();
    vm.$on('message-created', vm.onMessageCreated);
    vm.$notificationManager.on('total-result-count', vm.setupMemberCount);
    if (vm.canPublish) {
      vm.fetchScheduled({ silent: true });
    }
  },
  mounted() {
    this.muted = this.membership.muted;
    this.pinned = !!this.membership.pinned;
  },
  destroyed() {
    const vm = this;
    vm.$off('message-created', vm.onMessageCreated);
    vm.$notificationManager.off('total-result-count', vm.setupMemberCount);

    if (vm.pollInterval !== null) {
      clearInterval(vm.pollInterval);
    }
  },
  updated() {
    LocalTime.run();
  },
  computed: {
    itemEditorTitle() {
      return this.editorItem && this.editorItem.status === 'scheduled'
        ? 'Edit scheduled message'
        : 'Edit message';
    },
    canPublish() {
      return this.membership.can_publish;
    },
    canChangeMembers() {
      return this.admin;
    },
    editLink() {
      return `/message_groups/${this.group.id}/edit`;
    },
    indexHref() {
      return '/messages';
    },
    memberUrl() {
      return this.$apiService.messageMembershipsUrl(this.group.id);
    },
    subtitleText() {
      if (this.showMemberCount) {
        return `${this.memberCount} ${this.memberCount === 1 ? 'member' : 'members'}`;
      }
      if (this.group.group_type === 'direct') {
        return 'Direct message';
      }
      return '';
    },
    avatarInitials() {
      return avatarInitials(this.group.name);
    },
    avatarColor() {
      return avatarColor(this.group.id);
    },
    // A 2-person direct conversation shows the other participant's photo (or
    // their initials); anything else shows a group glyph. Mirrors the
    // conversation list. Distinct from isPair, which is only about message
    // layout and doesn't care whether the group is a direct.
    isDirectPair() {
      return this.group.group_type === 'direct' && (this.group.memberships || []).length === 2;
    },
    otherAvatarUrl() {
      if (this.avatarError || !this.isDirectPair) return null;
      const other = this.group.memberships.find(
        (m) => m.profile.profile_id !== this.activeProfileId
          || m.profile.profile_type !== this.activeProfileType,
      );
      return (other && other.profile.avatar_url) || null;
    },
    // A 2-person conversation is treated like a DM: no per-message avatars
    // (the header avatar is enough), matching iMessage/WhatsApp and our apps.
    isPair() {
      return (this.group.memberships || []).length === 2;
    },
    groupedMessages() {
      const today = moment().startOf('day');
      const yesterday = moment().subtract(1, 'day').startOf('day');
      const weekAgo = moment().subtract(6, 'days').startOf('day');
      const result = [];
      let currentDay = null;
      this.messages.forEach((message, idx) => {
        // Group by send time (matching the server's sent_at ordering), falling
        // back to created_at like the COALESCE the index uses — otherwise a
        // message scheduled one day and sent another lands under the wrong divider.
        const m = moment(message.sent_at || message.created_at);
        const day = m.format('YYYY-MM-DD');
        if (day !== currentDay) {
          currentDay = day;
          let label;
          if (m.isSame(today, 'day')) label = 'Today';
          else if (m.isSame(yesterday, 'day')) label = 'Yesterday';
          else if (m.isAfter(weekAgo)) label = m.format('dddd');
          else if (m.isSame(today, 'year')) label = m.format('MMMM D');
          else label = m.format('MMMM D, YYYY');
          result.push({ type: 'divider', day, label });
        }
        // firstOfRun marks the first message of a consecutive same-author,
        // same-day run. Only it renders the avatar + name + time; continuation
        // messages just stack the bubble beneath (Slack/Discord style). A day
        // change (which inserts a divider) also starts a new run.
        const prev = this.messages[idx - 1];
        const sameAuthorPrev = prev
          && prev.author.id === message.author.id
          && prev.author.author_type === message.author.author_type
          && moment(prev.sent_at || prev.created_at).format('YYYY-MM-DD') === day;
        result.push({ type: 'message', message, firstOfRun: !sameAuthorPrev });
      });
      return result;
    },
  },
  methods: {
    setupPolling() {
      const vm = this;
      if (vm.pollInterval !== null) return;
      vm.pollInterval = setInterval(() => {
        vm.checkForNewMessages();
      }, 25000);
    },
    setupMemberCount(objectType, count) {
      if (objectType === 'MessageMembership') {
        this.memberCount = parseInt(count, 10) || 0;
        this.showMemberCount = true;
      }
    },
    isEmpty() {
      return this.messagesLoaded && this.count === '0';
    },
    getPageParams(page, perPage) {
      return `?page=${page}&per_page=${perPage}`;
    },
    setNoMoreData() {
      const vm = this;
      vm.nextPageUrl = null;
      vm.noMoreData = true;
    },
    setupHistory(headers) {
      const vm = this;
      vm.count = headers.totalcount;

      if (!headers.link) {
        vm.setNoMoreData();
        return;
      }

      const paginationLinks = headers.link.split(',');
      let nextLinkDetected = false;
      paginationLinks.forEach((l) => {
        if (l.indexOf('rel="next"') !== -1) {
          const regExp = /\<([^>]+)>/;
          vm.nextPageUrl = regExp.exec(l)[1];
          nextLinkDetected = true;
        }
      });

      if (!nextLinkDetected) {
        vm.setNoMoreData();
      }
    },
    checkForNewMessages() {
      const vm = this;
      const url = vm.$apiService.messagesUrl(vm.group.id) + vm.getPageParams(1, vm.perPage);

      axios.get(url)
        .then((response) => {
          const updatedCount = response.headers.totalcount;
          if (updatedCount !== vm.count) {
            vm.resetListResults();
            vm.loadMessages(response);
            const firstM = response.data.messages[0];
            if (firstM) {
              const isOwnMessage = firstM.author.id === vm.activeProfileId
                && firstM.author.author_type === vm.activeProfileType;
              if (!isOwnMessage) {
                vm.$notificationManager.emit('show-toast', 'New message', true);
              }
            }
          }
        })
        .catch(() => {
          console.log('error checking for new messages');
        });
    },
    getMessages() {
      const vm = this;
      vm.resetListResults();
      vm.loading = true;
      const url = vm.$apiService.messagesUrl(vm.group.id) + vm.getPageParams(vm.page, vm.perPage);
      axios.get(url)
        .then((response) => {
          vm.loading = false;
          vm.loadMessages(response);
          vm.setupPolling();
        })
        .catch(() => {
          vm.resetListResults();
          vm.errorMessage = 'Error retrieving messages';
          vm.error = true;
        });
    },
    loadMessages(response) {
      const vm = this;
      vm.messages = vm.getReversedData(response.data.messages);
      vm.setupHistory(response.headers);
      vm.messagesLoaded = true;
      vm.$nextTick(() => {
        if (vm.highlightMessageId) {
          vm.loadUntilHighlighted();
        } else {
          vm.scrollToBottom();
        }
        vm.markAsRead();
      });
    },
    loadUntilHighlighted() {
      const vm = this;
      const found = vm.messages.find((m) => m.id === vm.highlightMessageId);
      if (found) {
        vm.scrollToHighlightedMessage();
        return;
      }
      if (!vm.nextPageUrl) {
        vm.scrollToBottom();
        return;
      }

      vm.loadingMore = true;
      vm.page += 1;
      axios.get(vm.nextPageUrl)
        .then((response) => {
          vm.loadingMore = false;
          const reversedM = response.data.messages;
          _.reverse(reversedM);
          vm.messages = reversedM.concat(vm.messages);
          vm.setupHistory(response.headers);
          vm.$nextTick(() => {
            vm.loadUntilHighlighted();
          });
        })
        .catch(() => {
          vm.loadingMore = false;
          vm.scrollToBottom();
        });
    },
    scrollToHighlightedMessage() {
      const vm = this;
      vm.$nextTick(() => {
        const el = document.getElementById(`message-${vm.highlightMessageId}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    },
    getReversedData(data) {
      _.reverse(data);
      return data;
    },
    scrollToBottom() {
      const container = this.$refs.scroller;
      if (container) container.scrollTop = container.scrollHeight;
    },
    markAsRead() {
      const vm = this;
      const url = vm.$apiService.readMessagesUrl(vm.group.id);
      axios.post(url, {})
        .then(() => {
          vm.$notificationManager.emit('message-read');
        })
        .catch(() => {
          console.log('failed to mark as read');
        });
    },
    getMoreMessages() {
      const vm = this;
      if (vm.loadingMore || !vm.nextPageUrl) return;

      vm.loadingMore = true;
      vm.page += 1;
      axios.get(vm.nextPageUrl)
        .then((response) => {
          vm.loadingMore = false;

          const container = vm.$refs.scroller;
          const previousHeight = container ? container.scrollHeight : 0;

          const reversedM = response.data.messages;
          _.reverse(reversedM);
          vm.messages = reversedM.concat(vm.messages);

          vm.$nextTick(() => {
            if (container) {
              const newHeight = container.scrollHeight;
              container.scrollTop = newHeight - previousHeight;
            }
          });
          vm.setupHistory(response.headers);
          vm.error = false;
        })
        .catch(() => {
          vm.resetListResults();
          vm.errorMessage = 'Error retrieving messages';
          vm.error = true;
        });
    },
    resetListResults() {
      const vm = this;
      vm.loading = false;
      vm.loadingMore = false;
      vm.noMoreData = false;
      vm.nextPageUrl = null;
      vm.page = 1;
      vm.perPage = 30;
      vm.messagesLoaded = false;
      vm.count = 0;
      vm.messages = [];
      vm.error = false;
      vm.errorMessage = '';
    },
    settingOpacity(setting) {
      return this.updatingSetting[setting] ? 0.25 : 1.0;
    },
    // Pin and mute are the same interaction against the same endpoint, so they
    // run through one path: dim while in flight, toast either outcome. Failures
    // toast rather than writing to `error`, which belongs to message loading.
    toggleSetting(setting) {
      if (this.updatingSetting[setting]) return;

      const labels = MEMBERSHIP_SETTINGS[setting];
      const newValue = !this[setting];
      const params = {
        message_membership: {
          id: this.membership.id,
          [setting]: newValue,
        },
      };
      this.updatingSetting[setting] = true;
      axios.put(this.$apiService.messageMembershipUrl(this.membership.id), params)
        .then(() => {
          this.updatingSetting[setting] = false;
          this[setting] = newValue;
          const label = newValue ? labels.on : labels.off;
          this.$notificationManager.emit('show-toast', `${label} ${this.group.name}`, true);
        })
        .catch(() => {
          this.updatingSetting[setting] = false;
          this.$notificationManager.emit('show-toast', `Failed to toggle ${labels.noun} for ${this.group.name}`, false);
        });
    },
    onMessageCreated() {
      this.getMessages();
      if (this.canPublish) this.fetchScheduled({ silent: true });
    },
    setActiveTab(tab) {
      this.activeTab = tab;
      if (tab === 'scheduled' && !this.scheduledLoadedOnce) {
        this.fetchScheduled();
      }
    },
    fetchScheduled({ silent = false } = {}) {
      const vm = this;
      if (!silent) vm.queueLoading = true;
      const url = `${vm.$apiService.messagesUrl(vm.group.id)}/scheduled?per_page=100`;
      axios.get(url)
        .then((response) => {
          vm.queueLoading = false;
          vm.scheduled = response.data.messages || [];
          vm.scheduledLoadedOnce = true;
        })
        .catch(() => {
          vm.queueLoading = false;
        });
    },
    // Open the shared editor modal for a scheduled message or a sent-message edit.
    // Uses its own composer instance, leaving the inline live draft untouched.
    openItemEditor(msg) {
      this.editorItem = msg;
      this.showItemEditor = true;
    },
    closeItemEditor() {
      this.showItemEditor = false;
      this.editorItem = null;
    },
    onItemEditorSaved() {
      this.closeItemEditor();
      this.fetchScheduled({ silent: true });
      this.getMessages();
    },
    onItemEditorEdited(updated) {
      this.onMessageEdited(updated);
      this.closeItemEditor();
    },
    editSentMessage(msg) {
      this.openItemEditor(msg);
    },
    onMessageEdited(updated) {
      const idx = this.messages.findIndex((m) => m.id === updated.id);
      if (idx !== -1) this.$set(this.messages, idx, updated);
    },
    sendScheduledNow(msg) {
      const vm = this;
      if (!window.confirm('Send this message now?')) return;
      // Transition the scheduled row to sent in place via its own client_token
      // (idempotent upsert); status defaults to sent and the content is preserved.
      const params = { message: { client_token: msg.client_token, status: 'sent' } };
      axios.post(vm.$apiService.messagesUrl(vm.group.id), params)
        .then(() => {
          vm.scheduled = vm.scheduled.filter((s) => s.id !== msg.id);
          vm.getMessages();
          vm.$notificationManager.emit('show-toast', 'Message sent', true);
        })
        .catch(() => {
          vm.$notificationManager.emit('show-toast', 'Unable to send', false);
        });
    },
    deleteQueueItem(msg) {
      const vm = this;
      if (!window.confirm('Delete this message?')) return;
      const url = `${vm.$apiService.messagesUrl(vm.group.id)}/${msg.id}`;
      axios.delete(url)
        .then(() => {
          vm.scheduled = vm.scheduled.filter((s) => s.id !== msg.id);
          vm.$notificationManager.emit('show-toast', 'Deleted', true);
        })
        .catch(() => {
          vm.$notificationManager.emit('show-toast', 'Unable to delete', false);
        });
    },
    onScheduledSaved() {
      this.fetchScheduled({ silent: true });
    },
    previewForQueueItem(msg) {
      if (msg.rich_content && msg.rich_content.length > 0) return msg.rich_content;
      if (msg.content) return _.escape(msg.content);
      // An attachment-only message isn't empty — its attachments render below.
      if (msg.attachments && msg.attachments.length > 0) return '';
      return '<em>(empty)</em>';
    },
    formatTimestamp(ts) {
      return moment(ts).fromNow();
    },
    formatScheduledAt(ts) {
      const m = moment(ts);
      const today = moment().startOf('day');
      if (m.isSame(today, 'day')) return `today at ${m.format('h:mma')}`;
      if (m.isSame(today.clone().add(1, 'day'), 'day')) return `tomorrow at ${m.format('h:mma')}`;
      return m.format('MMM D [at] h:mma');
    },
    onSyncClickFromModal() {
      this.showDetailsModal = false;
      this.showRosterSync = true;
    },
    openRosterSync() {
      this.showRosterSync = true;
    },
    onRosterSyncSaved() {
      // Backfill runs async via Sidekiq; reload so new members show up
      // in the list and count once the job settles.
      window.location.reload();
    },
  },
};
</script>
